import type { Json } from '@/lib/database.types';
import { supabase } from '@/lib/supabase';

/**
 * Shared grid preferences, per organization.
 *
 * TMS spread seven near-identical `upsert` calls across `gridHeaders.ts` and
 * `gridDataFormats.ts`, each writing its own key into a single-tenant `ajustes`
 * table. They collapse into one module here, and every call takes an explicit
 * `orgId`: a grid preference belongs to a tenant, and a formula evaluating over
 * another tenant's columns would be a data leak rather than a cosmetic bug.
 *
 * `org_id` is passed rather than filtered on trust — RLS on `grid_settings` is
 * what actually enforces it (CLAUDE.md, invariant 3). Reads need membership;
 * writes need admin, because these settings are shared by everyone in the
 * organization.
 */

export const GRID_SETTING_KEYS = {
  headers: 'grid_headers',
  columnOrder: 'grid_column_order',
  rowOrder: 'grid_row_order',
  sort: 'grid_sort',
  grouping: 'grid_grouping',
  widths: 'grid_column_widths',
  hiddenColumns: 'grid_hidden_columns',
  /** Per grid: outline groups of columns that fold behind a +/− button (`ColumnGroup`). */
  columnGroups: 'grid_column_groups',
  frozenColumns: 'grid_frozen_columns',
  /** Per grid: columns shown on only one device (`GridColumnDevices`). */
  columnDevices: 'grid_column_devices',
  alignment: 'grid_alignment',
  colors: 'grid_colors',
  /** Per grid: field -> one default fill for the entire column. */
  columnColors: 'grid_column_colors',
  /** Per grid: field -> one fill for that column's heading. */
  headerColors: 'grid_header_colors',
  /**
   * The organization's ThemeColors — the whole site's palette, not the grid's.
   * It lives here rather than in a table of its own because `grid_settings` is
   * already the per-organization jsonb store with the document override split
   * this needs. `colors` above is its predecessor and is read once, to upgrade.
   */
  theme: 'theme_colors',
  /** The organization's sizes and grid lines (`ThemeLayout`), edited beside the theme. */
  layout: 'theme_layout',
  dataFormats: 'grid_data_formats',
  formulas: 'grid_formulas',
  globalFormats: 'grid_global_formats',
  decimalSeparator: 'grid_decimal_separator',
  /** The currencies a currency column may be shown in. */
  currencies: 'grid_currencies',
  itemNames: 'grid_item_names',
  /** Per grid: whether group headers count their items. Shared, like every
   *  other display decision — everyone reading the grid should read it the
   *  same way. */
  groupCounts: 'grid_group_counts',
  /** Per grid: the fields that show a "last change time" column beside them. */
  changeTimeColumns: 'grid_change_time_columns',
  /** Per grid: field → the note shown when the pointer rests on its header. */
  headerNotes: 'grid_header_notes',
  /** Per grid: the fields whose subtotal or totals-row value is hidden. */
  hiddenAggregates: 'grid_hidden_aggregates',
  /** Per grid: summary cards visibility/placement, totals row and group
   *  subtotals. Shared, so every reader of a sheet sees the same aggregates. */
  aggregateDisplay: 'grid_aggregate_display',
  /** Placement of sheet tabs: 'top' | 'bottom' | 'left' */
  tabsPlacement: 'grid_tabs_placement',
  /** Placement of sheet tabs on mobile; unset follows `tabsPlacement`. */
  tabsPlacementMobile: 'grid_tabs_placement_mobile',
  /** Custom configured summary cards per grid/sheet */
  summaryCards: 'grid_summary_cards',
  /** Per grid: the chart panel's type, columns, labels and sort. Shared, so
   *  everyone opening the sheet sees the same chart. */
  chart: 'grid_chart',
  /** Tab form configurations per grid/sheet */
  forms: 'grid_forms',
} as const;

export type GridSettingKey =
  (typeof GRID_SETTING_KEYS)[keyof typeof GRID_SETTING_KEYS];

/** Every stored preference for one organization, keyed as above. */
export type GridSettings = Partial<Record<GridSettingKey, unknown>>;

/**
 * Load the whole set in one round trip. A grid needs most of these at mount,
 * and eleven separate queries to render one screen is the kind of thing that
 * only shows up as slowness once a customer has real data.
 */
/**
 * Every column rename in the organization, merged into one `gridKey → field →
 * name` map. Renames live in each document's `grid_headers` blob, and a column
 * picker that lists tables from many documents needs them all; the keys embed a
 * tab id, so merging documents into one map cannot collide.
 */
export async function loadAllGridHeaders(
  orgId: string,
  signal?: AbortSignal,
): Promise<Record<string, Record<string, string>>> {
  let query = supabase
    .from('grid_settings')
    .select('value')
    .eq('org_id', orgId)
    .eq('key', GRID_SETTING_KEYS.headers);
  if (signal) query = query.abortSignal(signal);

  const { data, error } = await query;
  if (error) throw error;

  const merged: Record<string, Record<string, string>> = {};
  for (const row of (data ?? []) as { value: unknown }[]) {
    if (!row.value || typeof row.value !== 'object' || Array.isArray(row.value)) continue;
    for (const [gridKey, names] of Object.entries(row.value as Record<string, unknown>)) {
      if (!names || typeof names !== 'object' || Array.isArray(names)) continue;
      const renames: Record<string, string> = { ...(merged[gridKey] ?? {}) };
      for (const [field, name] of Object.entries(names as Record<string, unknown>)) {
        if (typeof name === 'string' && name !== '') renames[field] = name;
      }
      merged[gridKey] = renames;
    }
  }
  return merged;
}

export async function loadGridSettings(
  orgId: string,
  documentId?: string | null,
): Promise<GridSettings> {
  let query = supabase.from('grid_settings').select('key, value, document_id').eq('org_id', orgId);

  // When a documentId is given, load both the organization-wide settings
  // (global formats, decimal separator, palette) and document-specific overrides.
  query = documentId ? query.eq('document_id', documentId) : query.is('document_id', null);

  const { data, error } = await query;

  if (error) throw error;

  const settings: GridSettings = {};
  const rows = (data ?? []) as { key: string; value: unknown; document_id: string | null }[];

  // 1. Apply organization-wide settings first. Inside a document they come from the
  // document's owning organization through `document_org_grid_settings`, which a reader
  // from another organization can also call - the direct read hides org-wide rows from
  // non-members, and every reader must see the same cells (AGENTS.md invariant 12).
  if (documentId) {
    const { data: orgWide, error: orgWideError } = await supabase.rpc('document_org_grid_settings', { p_document: documentId });
    if (orgWideError) throw orgWideError;
    if (orgWide && typeof orgWide === 'object' && !Array.isArray(orgWide)) Object.assign(settings, orgWide);
  } else {
    for (const row of rows.filter((r) => !r.document_id)) {
      settings[row.key as GridSettingKey] = row.value;
    }
  }
  // 2. Apply document-specific overrides on top
  if (documentId) {
    for (const row of rows.filter((r) => r.document_id === documentId)) {
      settings[row.key as GridSettingKey] = row.value;
    }
  }
  return settings;
}

/**
 * Write one preference. Returns the error rather than throwing, because every
 * caller is a UI event handler that has to keep the grid usable when the save
 * fails — the same contract TMS's save functions had.
 */
export async function saveGridSetting(
  orgId: string,
  key: GridSettingKey,
  value: Json,
  documentId?: string | null,
): Promise<Error | null> {
  // An RPC rather than `.upsert()`: the uniqueness is enforced by two PARTIAL
  // indexes (one per `document_id is null` branch), and PostgREST's conflict
  // target is a bare column list that Postgres cannot match to a partial index.
  // See `20260803001446_grid_upsert_rpcs.sql`.
  // The generated type makes a defaulted argument optional rather than
  // nullable, and `exactOptionalPropertyTypes` will not accept an explicit
  // `undefined` for one — so "organization-wide" is expressed by leaving the
  // argument out of the object entirely.
  const { error } = await supabase.rpc('save_grid_setting', {
    p_org_id: orgId,
    p_key: key,
    p_value: value,
    ...(documentId ? { p_document_id: documentId } : {}),
  });

  return error ?? null;
}
